<script setup lang="ts">
import type { FavoriteStoreOption } from '~/components/filters/FavoriteStoresFilterDropdown.vue'
import {
  BEST_ITEMS_AISLE_CACHE_KEY,
  BEST_ITEMS_PERIOD_CACHE_KEY,
  BEST_ITEMS_STORE_CACHE_KEY,
} from '~/stores/cache'

// Métadonnées de la page des meilleurs rabais
useHead({
  title: 'Meilleurs rabais - Glouton',
  meta: [
    { name: 'description', content: 'Découvrez les meilleurs rabais et promotions des circulaires de vos commerces favoris.' },
    { property: 'og:title', content: 'Meilleurs rabais - Glouton' },
    { property: 'og:description', content: 'Découvrez les meilleurs rabais et promotions des circulaires de vos commerces favoris.' },
  ],
})

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    fr: '/meilleurs-rabais',
  },
})

definePageMeta({
  name: 'BestItemsPage',
})

const userStore = useUserStore()
const baseStore = useBaseStore()
const modalStore = useModalStore()
const cacheStore = useCacheStore()
const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const localePath = useLocalePath()
const { parseCachedAisleId } = useStoreAisles()
const { showCirculairesAds, shouldShowGridInlineAd, shouldShowListInlineAd, listInlineAdSizes, listInlineAdSizeMapping } = useCirculairesDisplayAds()

type BestItemsPeriod = 'current' | 'future'

interface FetchBestItemsResponse {
  success: number
  future_available?: boolean
  available_stores?: FavoriteStoreOption[]
  items?: {
    data: any[]
    total: number
    last_page: number
  }
}

// État de la page
const bestItems = ref<any[]>([])
const totalPages = ref(1)
const totalResults = ref(0)
const isLoading = ref(false)
const isPageLoading = ref(false)
const page = ref(Number.parseInt(route.query.page as string) || 1)
const displayMode = ref<'grid' | 'list'>('grid')
const itemsPerPage = computed(() => displayMode.value === 'list' ? 24 : 12)
const selectedAisleId = ref<number | null>(
  baseStore.showAisles
    ? parseCachedAisleId(cacheStore.getCache(BEST_ITEMS_AISLE_CACHE_KEY))
    : null,
)
const selectedPeriod = ref<BestItemsPeriod>(
  (cacheStore.getCache(BEST_ITEMS_PERIOD_CACHE_KEY) as BestItemsPeriod | undefined) ?? 'current',
)
const selectedStoreSlug = ref<string | null>(
  (cacheStore.getCache(BEST_ITEMS_STORE_CACHE_KEY) as string | null | undefined) ?? null,
)
const futureAvailable = ref(false)
const availableStores = ref<FavoriteStoreOption[]>([])

const periodOptions = computed(() => [
  { label: 'Offres en vigueur', value: 'current' },
  {
    label: 'Offres à venir',
    value: 'future',
    disabled: !futureAvailable.value,
  },
])

const emptyResultsMessage = computed(() => {
  if (selectedPeriod.value === 'future') {
    return 'Aucun rabais à venir ne correspond aux critères sélectionnés.'
  }
  if (baseStore.showAisles && selectedAisleId.value != null && selectedStoreSlug.value != null) {
    return 'Aucun rabais dans cette allée pour ce commerce.'
  }
  if (baseStore.showAisles && selectedAisleId.value != null) {
    return 'Aucun rabais dans cette allée pour vos commerces favoris.'
  }
  if (selectedStoreSlug.value != null) {
    const storeName = availableStores.value.find(s => s.slug === selectedStoreSlug.value)?.store_name
    return storeName
      ? `Aucun rabais pour ${storeName}. Les circulaires à venirs ne sont pas encore disponibles ou analysées.`
      : 'Aucun rabais pour ce commerce. Les circulaires à venirs ne sont pas encore disponibles ou analysées.'
  }
  return 'Aucun rabais ne correspond aux critères de sélection des meilleurs rabais.'
})

function onFilterChange() {
  if (page.value !== 1) {
    page.value = 1
  }
  else if (userStore.user) {
    fetchBestItems()
  }
}

function onAisleFilterChange() {
  onFilterChange()
}

// Récupérer les meilleurs rabais au chargement
onMounted(() => {
  if (userStore.user) {
    fetchBestItems()
  }
})

watch(() => userStore.user, (newUser) => {
  if (newUser) {
    fetchBestItems()
  }
})

watch(selectedPeriod, (value, oldValue) => {
  if (value === oldValue) {
    return
  }
  cacheStore.setCache(BEST_ITEMS_PERIOD_CACHE_KEY, selectedPeriod.value)
  onFilterChange()
})

watch(displayMode, () => {
  if (page.value !== 1) {
    page.value = 1
  }
  else if (userStore.user) {
    fetchBestItems()
  }
})

watch(() => baseStore.showAisles, (show) => {
  if (!show) {
    if (selectedAisleId.value != null) {
      selectedAisleId.value = null
    }
    cacheStore.clearCache(BEST_ITEMS_AISLE_CACHE_KEY)
    if (userStore.user) {
      onFilterChange()
    }
  }
})

async function fetchBestItems() {
  if (!userStore.user) {
    return
  }

  if (page.value === 1) {
    isLoading.value = true
  }
  else {
    isPageLoading.value = true
  }

  try {
    const response = await $fetch<FetchBestItemsResponse>(`${config.public.apiUrl}/flyer-reader/fetch-flyers-top-items`, {
      method: 'GET',
      query: {
        page: page.value,
        count: itemsPerPage.value,
        period: selectedPeriod.value,
        ...(baseStore.showAisles && selectedAisleId.value != null ? { aisle_id: selectedAisleId.value } : {}),
        ...(selectedStoreSlug.value ? { store_slug: selectedStoreSlug.value } : {}),
      },
      headers: {
        Authorization: `Bearer ${userStore.access_token}`,
      },
    })

    futureAvailable.value = response.future_available ?? false
    availableStores.value = response.available_stores ?? []

    if (selectedPeriod.value === 'future' && !futureAvailable.value) {
      selectedPeriod.value = 'current'
      cacheStore.setCache(BEST_ITEMS_PERIOD_CACHE_KEY, 'current')
      isLoading.value = false
      isPageLoading.value = false
      return fetchBestItems()
    }

    if (response.success === 1 && response.items?.data) {
      bestItems.value = response.items.data || []
      totalResults.value = response.items.total || 0
      totalPages.value = response.items.last_page || 1
    }
    else {
      bestItems.value = []
      totalResults.value = 0
      totalPages.value = 1
    }
  }
  catch (error: unknown) {
    if (error && typeof error === 'object' && 'name' in error) {
      console.error('Erreur lors de la récupération des meilleurs rabais:', error)
      bestItems.value = []
      totalResults.value = 0
      totalPages.value = 1
    }
  }
  finally {
    isLoading.value = false
    isPageLoading.value = false
  }
}

watch(page, () => {
  fetchBestItems()
  if (import.meta.client) {
    const el = document.getElementById('BestItemsPage-anchor')
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' })
    }
  }
})

watch(() => route.query.page, (newPage) => {
  if (newPage && typeof newPage === 'string') {
    const parsed = Number.parseInt(newPage) || 1
    if (parsed !== page.value) {
      page.value = parsed
    }
  }
})

watch(page, (newVal) => {
  router.push({
    query: {
      ...route.query,
      page: newVal,
    },
  })
})

function openLoginModal() {
  modalStore.openLoginModal()
}
</script>

<template>
  <div id="BestItemsPage" class="mt-0">
    <TopNav
      title="Meilleurs rabais"
      description="Découvrez les meilleurs rabais et promotions des circulaires de vos commerces favoris."
      access="Un compte gratuit est requis pour voir les scores et utiliser la liste d'épicerie."
      :total="totalResults"
      class="max-w-7xl mx-auto"
      :count="totalResults"
    />

    <UContainer id="BestItemsPage-anchor" class="pb-10 mt-6">
      <div v-if="userStore.user">
        <div class="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div class="flex flex-wrap items-center gap-3">
            <AisleFilterDropdown
              v-if="baseStore.showAisles"
              v-model="selectedAisleId"
              :persist-key="BEST_ITEMS_AISLE_CACHE_KEY"
              button-color="secondary"
              @change="onAisleFilterChange"
            />
            <FavoriteStoresFilterDropdown
              v-model="selectedStoreSlug"
              :available-stores="availableStores"
              :persist-key="BEST_ITEMS_STORE_CACHE_KEY"
              button-color="secondary"
              @change="onFilterChange"
            />
          </div>
          <UFieldGroup>
            <UButton
              color="secondary"
              active-variant="solid"
              variant="soft"
              icon="i-mdi-view-grid"
              :active="displayMode === 'grid'"
              class="px-3"
              @click="displayMode = 'grid'"
            />
            <UButton
              color="secondary"
              active-variant="solid"
              variant="soft"
              icon="i-mdi-view-list"
              :active="displayMode === 'list'"
              class="px-3"
              @click="displayMode = 'list'"
            />
          </UFieldGroup>
        </div>

        <div class="mb-4 flex justify-start">
          <USelect
            v-model="selectedPeriod"
            :items="periodOptions"
            class="w-48"
            icon="i-mdi-calendar"
          />
        </div>

        <UAlert
          v-if="selectedPeriod === 'future'"
          color="info"
          variant="soft"
          size="lg"
          icon="i-mdi-information"
          class="mb-4"
          orientation="horizontal"
          description="Les offres proviennent des circulaires à venir, certaines peuvent ne pas être encore disponibles ou analysées."
        />

        <div v-if="isLoading" class="text-center py-12">
          <UIcon name="i-mdi-loading" class="w-16 h-16 mx-auto text-poivre-400 mb-4 animate-spin" />
          <p class="text-poivre-600">
            Chargement des meilleurs rabais...
          </p>
        </div>

        <div v-else-if="bestItems.length > 0" class="space-y-6">
          <div v-if="displayMode === 'grid'" class="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:space-x-3">
            <div class="grid grid-cols-1 gap-6 pb-4 sm:grid-cols-2 lg:grid-cols-4">
              <template v-for="(item, index) in bestItems" :key="item.id">
                <div class="flex flex-col h-full">
                  <FlyerItemCard :item="item" />
                </div>
                <div
                  v-if="shouldShowGridInlineAd(index) && showCirculairesAds"
                  class="flex flex-col sm:col-span-2 md:col-span-1 lg:col-span-2"
                >
                  <DisplayAd
                    dfp-ad-unit-path="glouton/circulaires"
                    :sizes="[300, 250]"
                    custom-class="h-full"
                  />
                </div>
              </template>
            </div>
          </div>

          <div v-else class="overflow-hidden rounded-lg border border-gray-200 bg-white">
            <ul class="divide-y divide-gray-200">
              <template v-for="(item, index) in bestItems" :key="item.id">
                <FlyerItemListCard
                  :position="index"
                  :item="item"
                />
                <li
                  v-if="showCirculairesAds && shouldShowListInlineAd(index, bestItems.length)"
                  class="py-4 px-2"
                >
                  <DisplayAd
                    dfp-ad-unit-path="glouton/circulaires"
                    :sizes="listInlineAdSizes"
                    :size-mapping="listInlineAdSizeMapping"
                  />
                </li>
              </template>
            </ul>
          </div>

          <DisplayAd
            v-if="showCirculairesAds"
            dfp-ad-unit-path="glouton/circulaires"
            :sizes="[[320, 50], [728, 90]]"
            :size-mapping="[[[0, 0], [[320, 50]]], [[1024, 0], [[728, 90]]]]"
            custom-class="mb-6"
          />
          <Paginator
            class="flex justify-center mt-8"
            :total-pages="totalPages"
            :total="totalResults"
            :per-page="itemsPerPage"
            :current-page="page"
            @pagechanged="(newPage) => page = newPage"
          />
        </div>

        <div v-else class="text-center py-12">
          <UIcon name="i-mdi-star" class="w-16 h-16 mx-auto text-poivre-400 mb-4" />
          <h3 class="text-lg font-medium text-poivre-800 mb-2">
            Aucun rabais trouvé
          </h3>
          <p class="text-poivre-600 mb-4">
            {{ emptyResultsMessage }}
          </p>
          <UButton color="secondary" @click="router.push(localePath({ name: 'FlyersPage' }))">
            Voir toutes les circulaires
          </UButton>
        </div>
      </div>

      <div v-else class="text-center py-12">
        <UIcon name="i-mdi-star" class="w-16 h-16 mx-auto text-poivre-400 mb-4" />
        <h3 class="text-lg font-medium text-poivre-800 mb-2">
          Découvrez les meilleurs rabais
        </h3>
        <p class="text-poivre-600 mb-4">
          Connectez-vous pour voir les meilleurs rabais et promotions des circulaires de vos commerces favoris.
        </p>
        <div class="flex sm:flex-row gap-3 justify-center">
          <UButton color="secondary" variant="outline" @click="openLoginModal">
            Se connecter
          </UButton>
          <UButton color="secondary" :to="localePath({ name: 'RegisterPage' })">
            Créer un compte
          </UButton>
        </div>
      </div>
    </UContainer>
  </div>
</template>
